import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { pendingMutationCount } from "@/lib/offline/offlineDb";
import { syncOfflineQueue } from "@/lib/offline/offlineSyncEngine";
import {
  ADMIN_OPEN_ISSUES_QUERY_KEY,
} from "@/lib/adminOpenIssues";
import {
  CLEANER_CHECKLIST_QUERY_KEY,
  CLEANER_PROPERTIES_QUERY_KEY,
} from "@/lib/cleanerQueryKeys";

export function useOfflineSync() {
  const queryClient = useQueryClient();
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      setPendingCount(await pendingMutationCount());
    } catch (error) {
      console.warn("[useOfflineSync] count:", error);
    }
  }, []);

  const runSync = useCallback(async () => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      await refreshCount();
      return 0;
    }
    setIsSyncing(true);
    try {
      const synced = await syncOfflineQueue();
      if (synced > 0) {
        toast.success(`Zsynchronizowano ${synced} zapisów offline`);
        void queryClient.invalidateQueries({ queryKey: [CLEANER_CHECKLIST_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [CLEANER_PROPERTIES_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [ADMIN_OPEN_ISSUES_QUERY_KEY] });
      }
      await refreshCount();
      return synced;
    } catch (error) {
      console.error("[useOfflineSync] sync:", error);
      return 0;
    } finally {
      setIsSyncing(false);
    }
  }, [queryClient, refreshCount]);

  useEffect(() => {
    void refreshCount();
    void runSync();

    const onOnline = () => {
      void runSync();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void runSync();
      }
    };

    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
    const interval = window.setInterval(() => {
      void refreshCount();
    }, 15_000);

    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, [refreshCount, runSync]);

  return { pendingCount, isSyncing, runSync, refreshCount };
}
